import type { BoosterId } from 'src/game/minigame/match2-timestep/defs/booster';
import { duration } from 'src/replicant/utils/duration';
import { Match2PowerupType } from '../match2.ruleset';
import { createScheduleComponent } from '@play-co/components';

/**
 * Unlock level for magic lamps
 */
export const magicLampsUnlockLevel = 34;

/**
 * Possible rewards ids for magic lamps. These ids will be converted into
 * Match2RewardSet items when rewards are collected.
 */
export type MagicLampsRewardId =
  | BoosterId
  | Match2PowerupType
  | 'gems'
  | 'energy'
  | 'points'
  | 'unlimitedEnergy'
  | 'unlimitedBomb'
  | 'unlimitedRocket'
  | 'unlimitedDart'
  | 'grandPrize';

/**
 * Prizes are lamp contents: any of the possible rewards or a trap
 */
export type MagicLampsPrizeId = MagicLampsRewardId | 'trap';

/**
 * Outcome are the raw definition of what can be found in each lamp, for the ruleset only
 * The special types 'typeA', 'typeB' and 'typeC' are used to select a random reward from the extraRewards table
 */
export type MagicLampsOutcomeId =
  | MagicLampsPrizeId
  | 'typeA'
  | 'typeB'
  | 'typeC';

/**
 * Content of each lamp - can be a reward or a trap
 */
export type MagicLampsPrize = {
  type: MagicLampsPrizeId;
  value: number;
};

/**
 *  Possible outcomes for each lamp on each round
 */
export interface MagicLampsOutcome {
  type: MagicLampsOutcomeId;
  value?: number;
  chance: number;
}

/**
 * Magic lamps round definition
 */
export interface MatchLampsRound {
  stage: number;
  multiplier: number;
  outcomes: [
    MagicLampsOutcome,
    MagicLampsOutcome,
    MagicLampsOutcome,
    MagicLampsOutcome,
  ];
}

/**
 * Magic lamps event definition
 */
export interface MatchLampsEvent {
  /** Event id */
  id: string;
  /** Time when the event starts */
  startAt: number;
  /** Time when the event ends */
  endAt: number;
  /** Event participation cost in gems */
  entryCost: number;
  /** The final gems reward if the whole event is completed */
  grandPrize: number;
  /** Extra rewards packs available */
  extraRewards: {
    typeA: Partial<Record<MagicLampsPrizeId, number>>;
    typeB: Partial<Record<MagicLampsPrizeId, number>>;
    typeC: Partial<Record<MagicLampsPrizeId, number>>;
  };
}

export type MagicLampsTuning = 'control';

/**
 * List of rounds for the event, kept as an object for easy lookup
 * Tests should ensure that the rounds listed are in correct order and are making sense
 */
export const roundsTuning: Record<
  MagicLampsTuning,
  Record<string, MatchLampsRound>
> = {
  control: {
    round001: {
      stage: 1,
      multiplier: 1,
      outcomes: [
        {
          type: 'dart',
          value: 1,
          chance: 0,
        },
        {
          type: 'unlimitedBomb',
          value: duration({ minutes: 5 }),
          chance: 35,
        },
        {
          type: 'cube',
          value: 1,
          chance: 30,
        },
        {
          type: 'gems',
          value: 20,
          chance: 30,
        },
      ],
    },
    round002: {
      stage: 1,
      multiplier: 1,
      outcomes: [
        {
          type: 'trap',
          chance: 10,
        },
        {
          type: 'typeA',
          chance: 30,
        },
        {
          type: 'typeB',
          chance: 30,
        },
        {
          type: 'gems',
          value: 20,
          chance: 30,
        },
      ],
    },
    round003: {
      stage: 1,
      multiplier: 1,
      outcomes: [
        {
          type: 'trap',
          chance: 10,
        },
        {
          type: 'typeA',
          chance: 30,
        },
        {
          type: 'typeB',
          chance: 30,
        },
        {
          type: 'gems',
          value: 20,
          chance: 30,
        },
      ],
    },
    round004: {
      stage: 1,
      multiplier: 1,
      outcomes: [
        {
          type: 'trap',
          chance: 10,
        },
        {
          type: 'typeA',
          chance: 30,
        },
        {
          type: 'typeB',
          chance: 30,
        },
        {
          type: 'gems',
          value: 20,
          chance: 30,
        },
      ],
    },
    round005: {
      stage: 1,
      multiplier: 1,
      outcomes: [
        {
          type: 'trap',
          chance: 10,
        },
        {
          type: 'typeA',
          chance: 30,
        },
        {
          type: 'typeB',
          chance: 30,
        },
        {
          type: 'gems',
          value: 20,
          chance: 30,
        },
      ],
    },
    round006: {
      stage: 2,
      multiplier: 1.5,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeB',
          chance: 25,
        },
        {
          type: 'gems',
          value: 30,
          chance: 25,
        },
      ],
    },
    round007: {
      stage: 2,
      multiplier: 1.5,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeB',
          chance: 25,
        },
        {
          type: 'gems',
          value: 30,
          chance: 25,
        },
      ],
    },
    round008: {
      stage: 2,
      multiplier: 1.5,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeB',
          chance: 25,
        },
        {
          type: 'gems',
          value: 30,
          chance: 25,
        },
      ],
    },
    round009: {
      stage: 2,
      multiplier: 1.5,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeB',
          chance: 25,
        },
        {
          type: 'gems',
          value: 30,
          chance: 25,
        },
      ],
    },
    round010: {
      stage: 2,
      multiplier: 1.5,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeB',
          chance: 25,
        },
        {
          type: 'gems',
          value: 30,
          chance: 25,
        },
      ],
    },
    round011: {
      stage: 3,
      multiplier: 1.5,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeB',
          chance: 25,
        },
        {
          type: 'gems',
          value: 50,
          chance: 25,
        },
      ],
    },
    round012: {
      stage: 3,
      multiplier: 1.5,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeB',
          chance: 25,
        },
        {
          type: 'gems',
          value: 50,
          chance: 25,
        },
      ],
    },
    round013: {
      stage: 3,
      multiplier: 1.5,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 50,
          chance: 25,
        },
      ],
    },
    round014: {
      stage: 3,
      multiplier: 1.5,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 50,
          chance: 25,
        },
      ],
    },
    round015: {
      stage: 3,
      multiplier: 1.5,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 50,
          chance: 25,
        },
      ],
    },
    round016: {
      stage: 4,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 100,
          chance: 25,
        },
      ],
    },
    round017: {
      stage: 4,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 100,
          chance: 25,
        },
      ],
    },
    round018: {
      stage: 4,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeB',
          chance: 25,
        },
        {
          type: 'gems',
          value: 100,
          chance: 25,
        },
      ],
    },
    round019: {
      stage: 4,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 100,
          chance: 25,
        },
      ],
    },
    round020: {
      stage: 4,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 100,
          chance: 25,
        },
      ],
    },
    round021: {
      stage: 5,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 120,
          chance: 25,
        },
      ],
    },
    round022: {
      stage: 5,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeB',
          chance: 25,
        },
        {
          type: 'gems',
          value: 120,
          chance: 25,
        },
      ],
    },
    round023: {
      stage: 5,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 120,
          chance: 25,
        },
      ],
    },
    round024: {
      stage: 5,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 120,
          chance: 25,
        },
      ],
    },
    round025: {
      stage: 5,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 160,
          chance: 25,
        },
      ],
    },
    round026: {
      stage: 5,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeB',
          chance: 25,
        },
        {
          type: 'gems',
          value: 160,
          chance: 25,
        },
      ],
    },
    round027: {
      stage: 5,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 160,
          chance: 25,
        },
      ],
    },
    round028: {
      stage: 5,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 160,
          chance: 25,
        },
      ],
    },
    round029: {
      stage: 5,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeA',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 160,
          chance: 25,
        },
      ],
    },
    round030: {
      stage: 5,
      multiplier: 2,
      outcomes: [
        {
          type: 'trap',
          chance: 25,
        },
        {
          type: 'typeB',
          chance: 25,
        },
        {
          type: 'typeC',
          chance: 25,
        },
        {
          type: 'gems',
          value: 160,
          chance: 25,
        },
      ],
    },
  },
};

/**
 * Current magic lamps event
 */
export const magicLampsEvent: MatchLampsEvent = {
  id: 'magicLampsWeekly',
  startAt: new Date('Sep 11 2023 00:00:00 GMT+9').getTime(),
  endAt: new Date('Sep 12 2023 23:59:00 GMT+9').getTime(),
  entryCost: 600,
  grandPrize: 5000,
  extraRewards: {
    typeA: {
      unlimitedRocket: duration({ seconds: 300 }),
      bomb: 1,
      cube: 1,
      energy: 1,
    },
    typeB: {
      dart: 1,
      bullet: 1,
      drill: 1,
      roulette: 1,
    },
    typeC: {
      gems: 150,
    },
  },
};

/**
 * Continue cost for each use. Will repeat after the last entry.
 */
export const magicLampsContinueCost = [200, 400, 1000, 1500, 2000, 2500, 3000];

/**
 * Magic lamps schedule, from Monday 00:00 to Tuesday 23:59 every week
 */
export const magicLampsSchedule = createScheduleComponent(
  '2023-09-11',
  'Asia/Tokyo',
  {
    weekly: [{ day: 'Monday', duration: { days: 1, hours: 23, minutes: 59 } }],
  },
);
